/**
 * @module GoogleMapsLoader
 *
 * Loads the Google Maps JavaScript API on demand, once per page.
 *
 * The API can only be script-injected, and injecting it twice logs a warning and
 * re-initialises the namespace, so every consumer has to share one load. Keeping the
 * promise here is what makes concurrent callers safe: the second caller awaits the first
 * one's script rather than adding another.
 *
 * Loading is deliberately lazy. Maps JavaScript API loads are billed per load, so the
 * script must not be fetched on app start, only when a map is actually opened.
 */

/**
 * Google Maps API key. Configure via the VUE_APP_GOOGLE_MAPS_API_KEY environment
 * variable. The same key serves the Geocoding API (see `GeocodingService`); it needs the
 * Maps JavaScript API enabled on it as well.
 */
const GOOGLE_MAPS_API_KEY: string = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || "";

const SCRIPT_ID = "google-maps-script";

/**
 * The in-flight or settled load. Held for the lifetime of the page so repeat calls are
 * free.
 */
let loadPromise: Promise<void> | null = null;

/** Whether the API is already usable, without triggering a load. */
export function isGoogleMapsReady(): boolean {
  return !!window.google?.maps?.Map;
}

/**
 * Loads the Google Maps JavaScript API, or resolves immediately if it is already there.
 *
 * @param libraries - optional additional libraries (for example `places`). Only honoured
 *   on the first call, since the script is loaded once; later calls resolve against the
 *   already-loaded script whatever they ask for.
 * @returns a promise that resolves when `window.google.maps` is usable.
 * @throws if the script fails to load, so callers can show a real error instead of
 *   silently rendering an empty box.
 */
export function loadGoogleMaps(libraries: string[] = []): Promise<void> {
  if (isGoogleMapsReady()) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      // Another module injected the tag without going through here. It may already have
      // finished, in which case no further load event is coming and waiting for one would
      // hang forever.
      if (isGoogleMapsReady()) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error("Failed to load the Google Maps API"))
        );
      }
      return;
    }

    const params = new URLSearchParams({ key: GOOGLE_MAPS_API_KEY });
    if (libraries.length > 0) {
      params.set("libraries", libraries.join(","));
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the cached failure so a later attempt can retry instead of being stuck with
      // a permanently rejected promise after, say, a brief network outage.
      loadPromise = null;
      script.remove();
      reject(new Error("Failed to load the Google Maps API"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
